import { useEffect, useMemo, useState } from 'react';
import { TrendingUp, PackageX, CalendarClock, Wallet } from 'lucide-react';
import { Card, Pill } from '@nako/ui';
import { formatMoney, expiryStatus, lowStockList } from '@nako/core';
import type { Business, PharmacyClaim, PharmacyDispense } from '@nako/types';
import { supabase } from '@/lib/supabase';
import type { PharmacyData } from '@/pages/Pharmacy';

export function PharmacyReports({ business, data }: { business: Business; data: PharmacyData }) {
  const [dispenses, setDispenses] = useState<PharmacyDispense[]>([]);
  const [claims, setClaims] = useState<PharmacyClaim[]>([]);

  useEffect(() => {
    const monthStart = new Date();
    monthStart.setDate(1);
    const from = monthStart.toISOString().slice(0, 10);
    void supabase
      .from('pharmacy_dispenses')
      .select('*')
      .eq('business_id', business.id)
      .eq('is_void', false)
      .gte('dispense_date', from)
      .then(({ data: rows }) => setDispenses((rows ?? []) as PharmacyDispense[]));
    void supabase
      .from('pharmacy_claims')
      .select('*')
      .eq('business_id', business.id)
      .is('deleted_at', null)
      .in('status', ['draft', 'submitted', 'part_paid'])
      .then(({ data: rows }) => setClaims((rows ?? []) as PharmacyClaim[]));
  }, [business.id]);

  const today = new Date().toISOString().slice(0, 10);

  const salesToday = useMemo(
    () => dispenses.filter((d) => d.dispense_date === today).reduce((s, d) => s + d.total, 0),
    [dispenses, today],
  );
  const salesMonth = useMemo(() => dispenses.reduce((s, d) => s + d.total, 0), [dispenses]);

  const low = useMemo(() => {
    const batchesByDrug = new Map(
      [...data.batchesByDrug].map(([k, v]) => [
        k,
        v.map((b) => ({ expiry_date: b.expiry_date, qty_on_hand: b.qty_on_hand })),
      ]),
    );
    return lowStockList(data.drugs, batchesByDrug, today);
  }, [data.drugs, data.batchesByDrug, today]);

  const expiringValue = useMemo(() => {
    let value = 0;
    for (const batches of data.batchesByDrug.values()) {
      for (const b of batches) {
        const band = expiryStatus(b.expiry_date, today).band;
        if (b.qty_on_hand > 0 && (band === 'expired' || band === 'expiring_90')) {
          value += b.qty_on_hand * b.unit_cost;
        }
      }
    }
    return value;
  }, [data.batchesByDrug, today]);

  const stockValue = useMemo(() => {
    let value = 0;
    for (const batches of data.batchesByDrug.values()) {
      for (const b of batches) value += b.qty_on_hand * b.unit_cost;
    }
    return value;
  }, [data.batchesByDrug]);

  const claimsOutstanding = useMemo(
    () => claims.reduce((s, c) => s + (c.total_claimed - c.total_paid), 0),
    [claims],
  );

  const drugById = useMemo(() => new Map(data.drugs.map((d) => [d.id, d])), [data.drugs]);

  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat icon={<TrendingUp size={16} />} label="Sales today" value={formatMoney(salesToday)} />
        <Stat
          icon={<TrendingUp size={16} />}
          label="Sales this month"
          value={formatMoney(salesMonth)}
        />
        <Stat icon={<Wallet size={16} />} label="Stock value" value={formatMoney(stockValue)} />
        <Stat
          icon={<Wallet size={16} />}
          label="Claims outstanding"
          value={formatMoney(claimsOutstanding)}
        />
      </div>

      <Card className="p-4">
        <div className="mb-2 flex items-center gap-2">
          <PackageX size={16} className="text-amber-600" />
          <h3 className="font-medium">Low stock ({low.length})</h3>
        </div>
        {low.length === 0 ? (
          <p className="text-muted text-sm">Everything is above its reorder level.</p>
        ) : (
          <div className="grid gap-1.5">
            {low.map((r) => (
              <div key={r.drug_id} className="flex items-center justify-between text-sm">
                <span>{drugById.get(r.drug_id)?.name ?? 'Unknown'}</span>
                <Pill tone="accent">
                  {r.on_hand} / reorder {r.reorder_level}
                </Pill>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-4">
        <div className="mb-2 flex items-center gap-2">
          <CalendarClock size={16} className="text-rose-600" />
          <h3 className="font-medium">Expiring / expired stock value</h3>
        </div>
        <p className="text-2xl font-semibold">{formatMoney(expiringValue)}</p>
        <p className="text-muted text-xs">
          Cost value of unexpired stock within 90 days plus already-expired stock still on hand.
        </p>
      </Card>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Card className="p-3">
      <div className="text-muted flex items-center gap-1.5 text-xs">
        {icon}
        {label}
      </div>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </Card>
  );
}
